import {action, Action, computed, Computed, thunk, Thunk} from 'easy-peasy';
import {jsonToFormData} from '~/app/helpers/headerContentType'
import {CostCategory, Customer, SavePartPayload, HPCode, Issue, IssueCategory, IssueClosePayload, IssueDetails, IssueImagePayload, IssuePayload, IssueResponsePayload, IssueSortingType, Message, WorkType, Part, SavePartResponse, SearchPartResponse, APIResponse, Label, IssueChatPayload, SearchUser, SaveChatUserResponse, SaveChatUserPayload, Material, MiscMaterial, SaveMaterialPayload, SaveMaterialResponse} from '~/types';
import {StoreModel} from "~/store";
import {API} from "~/app/routes";
import {CommonModel, fetch, fetchCancel, getCommonModel} from "~/store/commonStore";
import axios from "axios";
import { appReferenceRepository, issueRepository } from '~/data/repositories';
import { issueLocal } from '~/data/local';
import { connectivityService } from '~/data/connectivity/ConnectivityService';
import {IssueStatuses, IssueTypes} from "~/app/data/statuses/issue";
import {isIssueFinished, matchesIssueCategory} from "~/issues/helpers";

export interface IssueTabCounts {
    open: number
    completed: number
}

export interface IssueModel extends CommonModel<Issue> {
    categories: IssueCategory[]
    fetchingIssueCategories: boolean
    setFetchingIssueCategories: Action<IssueModel, boolean>
    fetchIssueCategories: Thunk<IssueModel, void, any, StoreModel, Promise<IssueCategory[]>>
    fetchIssueCategoriesCancel: Action<IssueModel>
    fetchedIssueCategories: Action<IssueModel, IssueCategory[]>

    issueUpdating: boolean
    setIssueUpdating: Action<IssueModel, boolean>
    saveIssue: Thunk<IssueModel, IssuePayload, any, StoreModel, Promise<IssueResponsePayload>>

    showCompleted: boolean
    setShowCompleted: Action<IssueModel, boolean>

    categorySearchText: string
    setCategorySearchText: Action<IssueModel, string>
    isCategorySearchOpen: boolean
    setIsCategorySearchOpen: Action<IssueModel, boolean>

    filteredIssues: Computed<IssueModel, Issue[], StoreModel>
    issueCounts: Computed<IssueModel, IssueTabCounts, StoreModel>
    filteredOpenIssues: Computed<IssueModel, Issue[], StoreModel>

    data: IssueDetails,
    fetchIssue: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    fetchedIssue: Action<IssueModel, IssueDetails>
    fetchFavoriteMachinesIssues: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    addChatMessage: Action<IssueModel, Message>
    addChatUser: Thunk<IssueModel, IssueChatPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    updateIssueImage: Thunk<IssueModel, IssueImagePayload, any, StoreModel, Promise<void>>

    closeIssue: Thunk<IssueModel, IssueClosePayload, any, StoreModel, Promise<APIResponse>>
    completeIssue: Thunk<IssueModel, IssueClosePayload, any, StoreModel, Promise<APIResponse>>

    issueChatUserUpdating: boolean
    setIssueChatUserUpdating: Action<IssueModel, boolean>
    tempChatUsers: SearchUser[]
    setTempChatUsers: Action<IssueModel, SearchUser[]>
    setChatUsers: Thunk<IssueModel, SearchUser[]>;
    fetchChatUsers: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    saveChatUser: Thunk<IssueModel, SaveChatUserPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    partUpdating: boolean
    setPartUpdating: Action<IssueModel, boolean>
    tempParts: Part[]
    setTempParts: Action<IssueModel, Part[]>
    setParts: Thunk<IssueModel, Part[]>;
    fetchParts: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    searchParts: Part[]
    searchPartsTotal: number
    setSearchedParts: Action<IssueModel, SearchPartResponse>
    fetchSearchParts: Thunk<IssueModel, { searchText: string, offset: number }, any, StoreModel, Promise<void>>
    savePart: Thunk<IssueModel, SavePartPayload, any, StoreModel, Promise<SavePartResponse>>

    materialUpdating: boolean
    setMaterialUpdating: Action<IssueModel, boolean>
    tempMaterials: Material[]
    setTempMaterials: Action<IssueModel, Material[]>
    setMaterials: Thunk<IssueModel, Material[]>;
    fetchMaterials: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    miscMaterials: MiscMaterial[]
    setMiscMaterials: Action<IssueModel, MiscMaterial[]>
    fetchMiscMaterials: Thunk<IssueModel, number, any, StoreModel, Promise<void>>
    saveMaterials: Thunk<IssueModel, SaveMaterialPayload, any, StoreModel, Promise<SaveMaterialResponse>>

    isWorkOrderFormActive: boolean
    setIsWorkOrderFormActive: Action<IssueModel, boolean>

    displayCustomer: boolean
    setDisplayCustomer: Action<IssueModel, boolean>

    displayProject: boolean
    setDisplayProject: Action<IssueModel, boolean>

    sortingTypes: IssueSortingType[]
    fetchingIssueSortingTypes: boolean
    setFetchingIssueSortingTypes: Action<IssueModel, boolean>
    fetchIssueSortingTypes: Thunk<IssueModel, void, any, StoreModel, Promise<IssueSortingType[]>>
    fetchIssueSortingTypesCancel: Action<IssueModel>
    fetchedIssueSortingTypes: Action<IssueModel, IssueSortingType[]>

    labels: Label[]
    fetchingLabels: boolean
    setFetchingLabels: Action<IssueModel, boolean>
    fetchLabels: Thunk<IssueModel, void, any, StoreModel, Promise<Label[]>>
    fetchLabelsCancel: Action<IssueModel>
    fetchedLabels: Action<IssueModel, Label[]>

    HPCodes: HPCode[]
    fetchingHPCodes: boolean
    setFetchingHPCodes: Action<IssueModel, boolean>
    fetchHPCodes: Thunk<IssueModel, void, any, StoreModel, Promise<HPCode[]>>
    fetchHPCodesCancel: Action<IssueModel>
    fetchedHPCodes: Action<IssueModel, HPCode[]>

    isHPCodeRequired: boolean
    setIsHPCodeRequired: Action<IssueModel, boolean>

    workTypes: WorkType[]
    fetchingWorkTypes: boolean
    setFetchingWorkTypes: Action<IssueModel, boolean>
    fetchWorkTypes: Thunk<IssueModel, void, any, StoreModel, Promise<WorkType[]>>
    fetchWorkTypesCancel: Action<IssueModel>
    fetchedWorkTypes: Action<IssueModel, WorkType[]>

    costCategories: CostCategory[]
    fetchingCostCategories: boolean
    setFetchingCostCategories: Action<IssueModel, boolean>
    fetchCostCategories: Thunk<IssueModel, IssueDetails['departmentId'], any, StoreModel, Promise<CostCategory[]>>
    fetchCostCategoriesCancel: Action<IssueModel>
    fetchedCostCategories: Action<IssueModel, CostCategory[]>

    customers: Customer[]
    fetchingCustomers: boolean
    setFetchingCustomers: Action<IssueModel, boolean>
    fetchCustomers: Thunk<IssueModel, void, any, StoreModel, Promise<Customer[]>>
    fetchCustomersCancel: Action<IssueModel>
    fetchedCustomers: Action<IssueModel, Customer[]>

    isReadOnlyChat: Computed<IssueModel, boolean, StoreModel>
}

export const issueModel: IssueModel = {
    ...getCommonModel<Issue>(API.issue),

    categories: [],
    fetchingIssueCategories: false,
    setFetchingIssueCategories: action((state, payload) => {
        state.fetchingIssueCategories = payload;
    }),
    fetchIssueCategories: thunk(async (actions) => {
        actions.setFetchingIssueCategories(true);
        try {
            const data = await appReferenceRepository.loadIssueCategories();
            data && actions.fetchedIssueCategories(data);
            return data || [];
        } finally {
            actions.setFetchingIssueCategories(false);
        }
    }),
    fetchIssueCategoriesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedIssueCategories: action((state, payload) => {
        state.categories = payload;
    }),

    issueUpdating: false,
    setIssueUpdating: action((state, payload) => {
        state.issueUpdating = payload;
    }),
    saveIssue: thunk(async (actions, payload) => {
        actions.setIssueUpdating(true);
        try {
            return await issueRepository.saveIssue(payload);
        } finally {
            actions.setIssueUpdating(false);
        }
    }),

    showCompleted: false,
    setShowCompleted: action((state, payload) => {
        state.showCompleted = payload;
    }),

    categorySearchText: '',
    setCategorySearchText: action((state, payload) => {
        state.categorySearchText = payload;
    }),
    isCategorySearchOpen: false,
    setIsCategorySearchOpen: action((state, payload) => {
        state.isCategorySearchOpen = payload;
    }),

    filteredIssues: computed(
        [
            state => state.showCompleted,
            state => state.categorySearchText,
            (state, storeState) => storeState.machine.data.issues
        ],
        (status, searchText, issues) => issues.filter(issue =>
            (status ? isIssueFinished(issue) : !isIssueFinished(issue)) && matchesIssueCategory(issue.categoryName, searchText)
        )
    ),
    issueCounts: computed(
        [
            state => state.categorySearchText,
            (state, storeState) => storeState.machine.data.issues
        ],
        (searchText, issues) => issues.reduce((counts, issue) => {
            if (!matchesIssueCategory(issue.categoryName, searchText)) {
                return counts;
            }
            if (isIssueFinished(issue)) {
                counts.completed++;
            } else {
                counts.open++;
            }
            return counts;
        }, { open: 0, completed: 0 })
    ),
    filteredOpenIssues: computed(
        [
            (state, storeState) => storeState.machine.data.issues
        ],
        (issues) => issues.filter(issue => !(issue.status === IssueStatuses.COMPLETED || issue.status === IssueStatuses.CLOSED) && [IssueTypes.CRITICAL, IssueTypes.MORECRITICAL].includes(issue.type))
    ),

    data: {
        id: '',
        type: 0,
        description: '',
        status: 0,
        statusDate: '',
        comment: '',
        seenDate: '',
        images: [],
        files: [],
        categoryId: '',
        categoryName: '',
        issueNo: '',
        machineId: '',
        machineIntern: '',
        machineName: '',
        departmentName: '',
        departmentId: '',
        issueDepartmentName: '',
        departmentPrefix: null,
        issueDepartmentId: '',
        workOrderId: '',
        workOrderNo: '',
        workDetails: [],
        workOrderCost: { overtime1Percentage: null, overtime2Percentage: null },
        chat: [],
        chatUsers: [],
        createdAt: '',
        updatedAt: '',
        dueDate: '',
        createdBy: '',
        closedAt: null,
        closedBy: null,
        issueCreatedUserId: null,
        issueCreatedBy: null,
        issueUpdatedBy: null,
        projectId: null,
        registrationDate: '',
        parts: [],
        materials: [],
        labels: ''
    },
    fetchIssue: thunk(async (actions, payload) => {
        const data = await issueRepository.getIssue(payload);
        data && actions.fetchedIssue(data);
    }),
    fetchedIssue: action((state, payload) => {
        state.data = payload;
    }),
    fetchFavoriteMachinesIssues: thunk(async (actions, payload) => {
        if (connectivityService.isOnline()) {
            await issueRepository.fetchAndCacheFavoriteIssues(payload);
        }
    }),

    updateIssueImage: thunk((actions, payload) => {
        actions.setIssueUpdating(true);
        return axios.post<null, void>(`${API.issue}/update-issue-image`, jsonToFormData(payload))
            .then(data => data)
            .finally(() => actions.setIssueUpdating(false))
    }),

    closeIssue: thunk((actions, payload) => {
        actions.setIssueUpdating(true);
        return axios.post<null, APIResponse>(`${API.issue}/close`, jsonToFormData(payload)).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setIssueUpdating(false);
        })
    }),
    completeIssue: thunk((actions, payload) => {
        actions.setIssueUpdating(true);
        return axios.post<null, APIResponse>(`${API.issue}/complete`, jsonToFormData(payload)).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setIssueUpdating(false);
        })
    }),

    issueChatUserUpdating: false,
    setIssueChatUserUpdating: action((state, payload) => {
        state.issueChatUserUpdating = payload;
    }),
    tempChatUsers: [],
    setTempChatUsers: action((state, payload) => {
      state.tempChatUsers = payload;
    }),
    setChatUsers: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.chatUsers = payload;
        state.tempChatUsers = payload;
        await issueLocal.updateIssueDetail(state.data);
    }),
    fetchChatUsers: thunk((actions, payload) => {
        if (String(payload ?? '').trim() === '') {
            return Promise.resolve();
        }
        return axios.post<null, SearchUser[]>(`${API.issueUsers}/fetch/${payload}`)
            .then(
                data => {
                    actions.setChatUsers(data);
                })
    }),
    saveChatUser: thunk((actions, payload) => {
        actions.setIssueChatUserUpdating(true);
        return axios.post<null, SaveChatUserResponse>(`${API.issueUsers}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setIssueChatUserUpdating(false);
        })
    }),
    addChatUser: thunk((actions, payload) => {
        return axios.post<null, SaveChatUserResponse>(`${API.issueUsers}/add`, payload).then(data => { return data; })
    }),
    addChatMessage: action((state, payload) => {
        Number(state.data.id) === Number(payload.issueId) && state.data.chat.push(payload);
    }),

    partUpdating: false,
    setPartUpdating: action((state, payload) => {
        state.partUpdating = payload;
    }),
    tempParts: [],
    setTempParts: action((state, payload) => {
        state.tempParts = payload;
    }),
    setParts: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.parts = payload;
        state.tempParts = payload;
        await issueLocal.updateIssueDetail(state.data);
    }),
    fetchParts: thunk((actions, payload) => {
        return axios.post<null, Part[]>(`${API.issueParts}/fetch/${payload}`)
            .then(
                data => {
                    actions.setParts(data);
                })
    }),
    searchParts: [],
    searchPartsTotal: 0,
    setSearchedParts: action((state, payload) => {
        state.searchParts = payload.parts;
        state.searchPartsTotal = payload.total;
    }),
    fetchSearchParts: thunk((actions, payload) => {
        actions.setPartUpdating(true);
        return axios.post<null, SearchPartResponse>(`${API.issueParts}/search`, payload).then(
            data => {
                actions.setSearchedParts(data);
            }
        ).finally(() => {
            actions.setPartUpdating(false);
        })
    }),
    savePart: thunk((actions, payload) => {
        actions.setPartUpdating(true);
        return axios.post<null, SavePartResponse>(`${API.issueParts}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setPartUpdating(false);
        })
    }),

    materialUpdating: false,
    setMaterialUpdating: action((state, payload) => {
        state.materialUpdating = payload;
    }),
    tempMaterials: [],
    setTempMaterials: action((state, payload) => {
        state.tempMaterials = payload;
    }),
    setMaterials: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.materials = payload;
        state.tempMaterials = payload;
        await issueLocal.updateIssueDetail(state.data);
    }),
    fetchMaterials: thunk((actions, payload) => {
        return axios.get<null, Material[]>(`${API.issueMaterials}/fetch/${payload}`)
            .then(data => {
                actions.setMaterials(data);
            });
    }),
    miscMaterials: [],
    setMiscMaterials: action((state, payload) => {
        state.miscMaterials = payload;
    }),
    fetchMiscMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.get<null, MiscMaterial[]>(`${API.issueMaterials}/misc/${payload}`)
            .then(data => {
                actions.setMiscMaterials((data ?? []).filter(m => Number(m.id) !== 0));
            })
            .finally(() => {
                actions.setMaterialUpdating(false);
            });
    }),
    saveMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SaveMaterialResponse>(`${API.issueMaterials}/save`, payload).then(
            data => data
        ).finally(() => {
            actions.setMaterialUpdating(false);
        });
    }),

    isWorkOrderFormActive: false,
    setIsWorkOrderFormActive: action((state, payload) => {
        state.isWorkOrderFormActive = payload;
    }),

    displayCustomer: false,
    setDisplayCustomer: action((state, payload) => {
        state.displayCustomer = payload;
    }),

    displayProject: false,
    setDisplayProject: action((state, payload) => {
        state.displayProject = payload;
    }),

    sortingTypes: [],
    fetchingIssueSortingTypes: false,
    setFetchingIssueSortingTypes: action((state, payload) => {
        state.fetchingIssueSortingTypes = payload;
    }),
    fetchIssueSortingTypes: thunk(async (actions) => {
        actions.setFetchingIssueSortingTypes(true);
        try {
            const data = await appReferenceRepository.loadIssueSortingTypes();
            data && actions.fetchedIssueSortingTypes(data);
            return data || [];
        } finally {
            actions.setFetchingIssueSortingTypes(false);
        }
    }),
    fetchIssueSortingTypesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedIssueSortingTypes: action((state, payload) => {
        state.sortingTypes = payload;
    }),

    HPCodes: [],
    fetchingHPCodes: false,
    setFetchingHPCodes: action((state, payload) => {
        state.fetchingHPCodes = payload;
    }),
    fetchHPCodes: thunk((actions) => {
        actions.setFetchingHPCodes(true);
        return fetch<HPCode>(`${API.workOrder}/HP-codes`)
            .then(data => {
                actions.fetchedHPCodes(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingHPCodes(false);
            });
    }),
    fetchHPCodesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedHPCodes: action((state, payload) => {
        state.HPCodes = payload;
    }),
    isHPCodeRequired: false,
    setIsHPCodeRequired: action((state, payload) => {
        state.isHPCodeRequired = payload;
    }),

    labels: [],
    fetchingLabels: false,
    setFetchingLabels: action((state, payload) => {
        state.fetchingLabels = payload;
    }),
    fetchLabels: thunk(async (actions) => {
        actions.setFetchingLabels(true);
        try {
            const data = await appReferenceRepository.loadLabels();
            data && actions.fetchedLabels(data);
            return data || [];
        } finally {
            actions.setFetchingLabels(false);
        }
    }),
    fetchLabelsCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedLabels: action((state, payload) => {
        state.labels = payload;
    }),

    workTypes: [],
    fetchingWorkTypes: false,
    setFetchingWorkTypes: action((state, payload) => {
        state.fetchingWorkTypes = payload;
    }),
    fetchWorkTypes: thunk((actions) => {
        actions.setFetchingWorkTypes(true);
        return fetch<WorkType>(`${API.workOrder}/work-types`)
            .then(data => {
                actions.fetchedWorkTypes(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingWorkTypes(false);
            });
    }),
    fetchWorkTypesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedWorkTypes: action((state, payload) => {
        state.workTypes = payload;
    }),

    costCategories: [],
    fetchingCostCategories: false,
    setFetchingCostCategories: action((state, payload) => {
        state.fetchingCostCategories = payload;
    }),
    fetchCostCategories: thunk((actions, payload) => {
        actions.setFetchingCostCategories(true);
        return fetch<CostCategory>(`${API.workOrder}/cost-category-list-by-department/${payload}`)
            .then(data => {
                actions.fetchedCostCategories(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingCostCategories(false);
            });
    }),
    fetchCostCategoriesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedCostCategories: action((state, payload) => {
        state.costCategories = payload;
    }),

    customers: [],
    fetchingCustomers: false,
    setFetchingCustomers: action((state, payload) => {
        state.fetchingCustomers = payload;
    }),
    fetchCustomers: thunk((actions, payload) => {
        actions.setFetchingCustomers(true);
        return fetch<Customer>(`${API.workOrder}/customers`)
            .then(data => {
                actions.fetchedCustomers(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingCustomers(false);
            });
    }),
    fetchCustomersCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedCustomers: action((state, payload) => {
        state.customers = payload;
    }),

    isReadOnlyChat: computed(
        [state => state.data.status],
        (status) => (status === IssueStatuses.COMPLETED || status === IssueStatuses.CLOSED)
    ),
};
